import React, { useState, useEffect } from "react";
import { FaPlus, FaSave, FaTrash, FaCalculator, FaCogs, FaUserShield, FaSync, FaEdit } from "react-icons/fa";
import payrollService from "../../services/payrollService";
import Notification from "../../components/UI/Notification";

const SalaryConfiguration = () => {
  const [structures, setStructures] = useState([
    {
      id: "below_30k",
      name: "Below ₹30,000 Monthly CTC",
      components: [],
    },
    {
      id: "above_30k",
      name: "Above ₹30,000 Monthly CTC",
      components: [],
    },
  ]);

const [ptRules, setPtRules] = useState([
    { gender: "Male", threshold: 0, default_amount: 200, february_amount: 300 },
    { gender: "Female", threshold: 25000, default_amount: 200, february_amount: 300 },
  ]);

  const [statutorySettings, setStatutorySettings] = useState({
    id: null,
    pf_limit: 15000,
    esic_limit: 21000,
    pf_employer_rate: 13,
    esic_employer_rate: 3.25
  });

  const [isPTEditing, setIsPTEditing] = useState(null); // null or index
  const [isStatutoryEditing, setIsStatutoryEditing] = useState(false);

  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    fetchConfiguration();
  }, []);

  const fetchConfiguration = async () => {
    setLoading(true);
    try {
      // 1. Fetch Salary Components (Priority)
      try {
        const compResponse = await payrollService.getSalaryComponents();
        const allComps = compResponse?.data || (Array.isArray(compResponse) ? compResponse : []);
        
        if (allComps && Array.isArray(allComps)) {
          setStructures(prev => prev.map(s => ({
            ...s,
            components: allComps.filter(c => c.structure_id === s.id)
          })));
        }
      } catch (e) {
        console.error("Failed to fetch salary components:", e);
      }

      // 2. Fetch PT Rules
      try {
        const ptResponse = await payrollService.getProfessionalTaxRules();
        const ptData = ptResponse?.data || (Array.isArray(ptResponse) ? ptResponse : null);
        if (ptData) {
          setPtRules(ptData);
        }
      } catch (e) {
        console.error("Failed to fetch PT rules:", e);
      }

      // 3. Fetch Statutory Settings
      try {
        const statutoryResponse = await payrollService.getStatutorySettings();
        let statData = statutoryResponse?.data || statutoryResponse;
        
        // Handle if response is an array
        if (Array.isArray(statData) && statData.length > 0) {
          statData = statData[0];
        }

        if (statData && typeof statData === 'object') {
          setStatutorySettings({
            id: statData.id,
            pf_limit: statData.pf_limit ?? 15000,
            esic_limit: statData.esic_limit ?? 21000,
            pf_employer_rate: (statData.pf_employer_rate * 100) || 12,
            esic_employer_rate: (statData.esic_employer_rate * 100) || 3.25
          });
        }
      } catch (e) {
        console.error("Failed to fetch statutory settings:", e);
      }
    } catch (error) {
      console.error("Critical error in salary configuration fetch:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStructure = async (structureId) => {
    setLoading(true);
    try {
      const structure = structures.find(s => s.id === structureId);
      if (payrollService.updateSalaryStructure) {
        await payrollService.updateSalaryStructure(structureId, structure);
      }
      setNotification({ type: 'success', message: `${structure.name} saved successfully!` });
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to save structure.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStatutory = async (type) => {
    setLoading(true);
    try {
      if (type === 'pt') {
        await payrollService.addProfessionalTaxRule({ pt_rules: ptRules });
        setNotification({ type: 'success', message: 'PT Rules saved successfully!' });
        setIsPTEditing(null);
      } else {
        const payload = { 
          pf_limit: statutorySettings.pf_limit, 
          esic_limit: statutorySettings.esic_limit, 
          pf_employer_rate: statutorySettings.pf_employer_rate / 100, 
          esic_employer_rate: statutorySettings.esic_employer_rate / 100 
        };
        
        if (statutorySettings.id) {
          await payrollService.updateStatutorySettings(statutorySettings.id, payload);
          setNotification({ type: 'success', message: 'Statutory Settings saved successfully!' });
          setIsStatutoryEditing(false);
        } else {
          setNotification({ type: 'error', message: 'No statutory setting ID found to update.' });
        }
      }
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to save settings.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveIndividualPT = async (idx) => {
    setLoading(true);
    try {
      const rule = ptRules[idx];
      if (rule.id) {
        await payrollService.editProfessionalTaxRule(rule.id, rule);
      } else {
        await payrollService.addProfessionalTaxRule(rule);
      }
      setNotification({ type: 'success', message: `PT Rules for ${rule.gender} saved!` });
      setIsPTEditing(null);
      fetchConfiguration(); // Refresh to get the actual IDs
    } catch (error) {
      console.error("PT Save Error:", error.response?.data);
      setNotification({ type: 'error', message: 'Failed to save PT rule. Check required fields.' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddPTRule = () => {
    const newRule = { gender: "", threshold: 0, default_amount: 0, february_amount: 0 };
    setPtRules([...ptRules, newRule]);
    setIsPTEditing(ptRules.length);
  };

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activeStructureId, setActiveStructureId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [editingComponentIndex, setEditingComponentIndex] = useState(null);
  const [form, setForm] = useState({
    id: null,
    code: "",
    name: "",
    type: "earning",
    calc: "fixed",
    value: "",
    base: []
  });

  const openModal = (structureId) => {
    setActiveStructureId(structureId);
    setIsEditing(false);
    setEditingComponentIndex(null);
    setForm({ id: null, code: "", name: "", type: "earning", calc: "fixed", value: "", base: [] });
    setModalError(null);
    setShowModal(true);
  };

  const openEditModal = (structureId, component, index) => {
    setActiveStructureId(structureId);
    setIsEditing(true);
    setEditingComponentIndex(index);
    setForm({
      id: component.id || null,
      code: component.code,
      name: component.name,
      type: component.type,
      calc: component.calc,
      value: component.value,
      base: component.base || []
    });
    setModalError(null);
    setShowModal(true);
  };

  const handleDeleteComponent = (structureId, index) => {
    const structure = structures.find(s => s.id === structureId);
    const component = structure.components[index];
    setDeleteTarget({ type: 'comp', structureId, index, componentName: component.name, id: component.id });
    setShowDeleteModal(true);
  };

  const handleDeletePTRule = (idx) => {
    const rule = ptRules[idx];
    setDeleteTarget({ 
      type: 'pt', 
      index: idx, 
      componentName: rule.gender || "New Rule", 
      id: rule.id 
    });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { type, structureId, index, id } = deleteTarget;
    
    if (id) {
      setLoading(true);
      try {
        if (type === 'pt') {
          await payrollService.deleteProfessionalTaxRule(id);
        } else {
          await payrollService.deleteSalaryComponent(id);
        }
        setNotification({ type: 'success', message: 'Deleted successfully!' });
      } catch (error) {
        setNotification({ type: 'error', message: 'Failed to delete.' });
        setShowDeleteModal(false);
        return;
      } finally {
        setLoading(false);
      }
    }

    if (type === 'pt') {
      setPtRules(prev => prev.filter((_, i) => i !== index));
    } else {
      setStructures(prev => prev.map(s => 
        s.id === structureId 
          ? { ...s, components: s.components.filter((_, i) => i !== index) } 
          : s
      ));
    }
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const handleSubmitComponent = async (e) => {
    e.preventDefault();
    setModalError(null);
    const componentData = {
      ...form,
      structure_id: activeStructureId,
      code: form.code || form.name.toLowerCase().replace(/\s+/g, "_"),
      value: parseFloat(form.value) || 0
    };

    setLoading(true);
    try {
      let response;
      if (isEditing && form.id) {
        response = await payrollService.updateSalaryComponent(form.id, componentData);
      } else {
        response = await payrollService.addSalaryComponent(componentData);
      }
      
      if (response && response.success === false) {
        throw new Error(response.message || 'Failed to save component');
      }

      // Handle wrapped or raw response
      const savedComponent = response?.data || (response && !response.success ? response : componentData);

      setStructures(prev => prev.map(s => {
        if (s.id === activeStructureId) {
          let newComponents = [...s.components];
          if (isEditing) {
            newComponents[editingComponentIndex] = savedComponent;
          } else {
            newComponents.push(savedComponent);
          }
          return { ...s, components: newComponents };
        }
        return s;
      }));
      setNotification({ type: 'success', message: `Component ${isEditing ? 'updated' : 'added'} successfully!` });
      setShowModal(false);
    } catch (error) {
      console.error("Submit Error:", error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to save component.';
      setModalError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 bg-gray-50 min-h-screen font-poppins">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">Salary Configuration</h1>
            <p className="text-gray-500 mt-1">Manage dynamic salary structures and statutory rules</p>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={fetchConfiguration}
              className="p-3 text-gray-400 hover:text-blue-600 bg-white rounded-xl border border-gray-100 shadow-sm transition-all"
              title="Sync from Server"
            >
              <FaSync className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {notification && (
          <Notification 
            type={notification.type} 
            message={notification.message} 
            onClose={() => setNotification(null)} 
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Structures */}
          <div className="lg:col-span-2 space-y-8">
            {structures.map((structure) => (
              <div key={structure.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-50 bg-gray-50/50 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                      <FaCalculator />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-800">{structure.name}</h2>
                  </div>
                  <div className="flex items-center gap-4">
                    <button 
                      onClick={() => openModal(structure.id)}
                      className="text-white font-medium flex items-center gap-1 transition-colors bg-blue-600 py-2 px-4 rounded-md"
                    >
                      <FaPlus size={12} /> Add Component
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="text-gray-600 text-md font-semibold uppercase tracking-wider ">
                          <th className="pb-4 font-semibold text-[11px]">Component Name</th>
                          <th className="pb-4 font-semibold text-[11px]">Code</th>
                          <th className="pb-4 font-semibold text-[11px]">Type</th>
                          <th className="pb-4 font-semibold text-[11px]">Calculation</th>
                          <th className="pb-4 font-semibold text-[11px]">Value/Base</th>
                          <th className="pb-4 font-semibold text-[11px]">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {structure.components.map((comp, idx) => (
                          <tr key={idx} className="group hover:bg-gray-50/50 transition-colors">
                            <td className="py-4 font-medium text-gray-700">{comp.name}</td>
                            <td className="py-4 text-gray-500 font-mono text-sm cursor-help" title="Unique system code">{comp.code}</td>
                            <td className="py-4">
                              <span className={`px-3 py-1 rounded-full text-[10px] font-semibold uppercase ${
                                comp.type === 'earning' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                              }`}>
                                {comp.type}
                              </span>
                            </td>
                            <td className="py-4 text-gray-600 capitalize text-sm">{comp.calc}</td>
                            <td className="py-4 font-mono text-sm text-blue-600">
                              {comp.calc === 'percentage' ? `${Math.trunc(comp.value)}% of ${comp.base.join('+')}` : 
                               comp.calc === 'balancing' ? 'Auto-Balance' : `₹${Math.trunc(comp.value)}`}
                            </td>
                             <td className="py-4">
                               <div className="flex items-center gap-2">
                                 <button 
                                   onClick={() => openEditModal(structure.id, comp, idx)}
                                   className="p-2 text-gray-400 hover:text-blue-500 transition-colors"
                                 >
                                   <FaEdit size={14} />
                                 </button>
                                 <button 
                                   onClick={() => handleDeleteComponent(structure.id, idx)}
                                   className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                                 >
                                   <FaTrash size={14} />
                                 </button>
                               </div>
                             </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Statutory Rules Sidebar */}
          <div className="space-y-8">
            {/* PT Rules */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                    <FaUserShield />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-800">PT Rules</h2>
                </div>
                <button 
                  onClick={handleAddPTRule}
                  className="p-2 text-white bg-blue-600 px-3 py-2 hover:bg-blue-500 rounded-lg transition-colors flex items-center gap-1 text-xs font-semibold"
                  disabled={isPTEditing !== null}
                >
                  <FaPlus size={10} /> Add Rule
                </button>
              </div>

              <div className="space-y-4">
                {ptRules.map((rule, idx) => (
                  <div key={idx} className="p-4 rounded-xl border border-gray-50 bg-gray-50/30">
                    <div className="flex justify-between items-center mb-4">
                      {isPTEditing === idx && !rule.id ? (
                        <input 
                          type="text"
                          placeholder="Gender/Category"
                          className="px-2 py-1 bg-white border border-gray-200 rounded text-sm font-semibold focus:ring-1 focus:ring-blue-500 outline-none w-1/2"
                          value={rule.gender}
                          onChange={(e) => {
                            const newRules = [...ptRules];
                            newRules[idx].gender = e.target.value;
                            setPtRules(newRules);
                          }}
                        />
                      ) : (
                        <p className="font-semibold text-gray-800">{rule.gender}</p>
                      )}
                      {isPTEditing === idx ? (
                        <div className="flex gap-2">
                          <button 
                             onClick={() => setIsPTEditing(null)}
                             className="text-[10px] text-gray-400 hover:text-gray-600 font-bold"
                          >
                            Cancel
                          </button>
                          <button 
                            onClick={() => handleSaveIndividualPT(idx)}
                            disabled={loading}
                            className="text-[10px] text-blue-600 hover:text-blue-700 font-bold flex items-center gap-1 disabled:text-gray-400"
                          >
                            {loading ? <FaSync size={10} className="animate-spin" /> : <FaSave size={10} />}
                            {loading ? "Saving..." : "Save"}
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-3">
                          <button 
                            onClick={() => handleDeletePTRule(idx)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            disabled={isPTEditing !== null}
                          >
                            <FaTrash size={12} />
                          </button>
                          <button 
                            onClick={() => setIsPTEditing(idx)}
                            className="text-[10px] text-blue-600 hover:text-blue-700 font-bold flex items-center gap-1"
                            disabled={isPTEditing !== null}
                          >
                            <FaEdit size={10} /> Edit
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <label className="text-[11px] font-semibold text-gray-400 uppercase">Threshold Amount</label>
                        {isPTEditing === idx ? (
                          <input 
                            type="number"
                            className="w-full mt-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium focus:ring-1 focus:ring-blue-500 outline-none"
                            value={rule.threshold}
                            onChange={(e) => {
                              const newRules = [...ptRules];
                              newRules[idx].threshold = parseFloat(e.target.value) || 0;
                              setPtRules(newRules);
                            }}
                          />
                         ) : (
                           <p className="text-sm text-gray-700 font-semibold">₹{Math.trunc(rule.threshold)}+</p>
                         )}
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-gray-400 uppercase">Standard</label>
                        {isPTEditing === idx ? (
                          <input 
                            type="number"
                            className="w-full mt-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium focus:ring-1 focus:ring-blue-500 outline-none"
                            value={rule.default_amount}
                            onChange={(e) => {
                              const newRules = [...ptRules];
                              newRules[idx].default_amount = parseFloat(e.target.value) || 0;
                              setPtRules(newRules);
                            }}
                          />
                         ) : (
                           <p className="text-sm text-gray-700 font-semibold">₹{Math.trunc(rule.default_amount)}</p>
                         )}
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-gray-400 uppercase">February</label>
                        {isPTEditing === idx ? (
                          <input 
                            type="number"
                            className="w-full mt-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium focus:ring-1 focus:ring-blue-500 outline-none"
                            value={rule.february_amount}
                            onChange={(e) => {
                              const newRules = [...ptRules];
                              newRules[idx].february_amount = parseFloat(e.target.value) || 0;
                              setPtRules(newRules);
                            }}
                          />
                         ) : (
                           <p className="text-sm text-gray-700 font-semibold">₹{Math.trunc(rule.february_amount)}</p>
                         )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Other Configs */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                  <FaCalculator />
                </div>
                <h2 className="text-xl font-semibold text-gray-800">ESIC & PF Settings</h2>
              </div>
              <div className="flex gap-2 mb-6">
                {isStatutoryEditing ? (
                  <>
                    <button 
                      onClick={() => setIsStatutoryEditing(false)}
                      className="flex-1 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-all"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={() => handleSaveStatutory('statutory')}
                      disabled={loading}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-all flex items-center justify-center gap-2 disabled:bg-blue-400"
                    >
                      {loading ? <FaSync className="animate-spin" /> : <FaSave />} 
                      {loading ? "Saving..." : "Save"}
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => setIsStatutoryEditing(true)}
                    className="w-full px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-semibold hover:bg-blue-100 transition-all flex items-center justify-center gap-2"
                  >
                    <FaEdit /> Edit Settings
                  </button>
                )}
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 hover:bg-gray-50 rounded-lg transition-colors">
                  <span className="text-sm font-medium text-gray-600">PF Gross Limit</span>
                  {isStatutoryEditing ? (
                    <input 
                      type="number"
                      className="w-24 px-2 py-1 bg-white border border-gray-200 rounded text-right text-sm font-medium focus:ring-1 focus:ring-orange-500 outline-none"
                      value={statutorySettings.pf_limit}
                      onChange={(e) => setStatutorySettings({...statutorySettings, pf_limit: parseFloat(e.target.value) || 0})}
                    />
                  ) : (
                    <span className="font-mono text-blue-600 font-medium">₹{Math.trunc(statutorySettings.pf_limit).toLocaleString()}</span>
                  )}
                </div>
                <div className="flex justify-between items-center p-3 hover:bg-gray-50 rounded-lg transition-colors">
                  <span className="text-sm text-gray-600">ESIC Eligibility</span>
                  {isStatutoryEditing ? (
                    <input 
                      type="number"
                      className="w-24 px-2 py-1 bg-white border border-gray-200 rounded text-right text-sm font-medium focus:ring-1 focus:ring-orange-500 outline-none"
                      value={statutorySettings.esic_limit}
                      onChange={(e) => setStatutorySettings({...statutorySettings, esic_limit: parseFloat(e.target.value) || 0})}
                    />
                  ) : (
                    <span className="font-mono text-blue-600 font-medium">₹{Math.trunc(statutorySettings.esic_limit).toLocaleString()}</span>
                  )}
                </div>
                <div className="flex justify-between items-center p-3 hover:bg-gray-50 rounded-lg transition-colors">
                  <span className="text-sm text-gray-600">Employer PF %</span>
                  {isStatutoryEditing ? (
                    <input 
                      type="number"
                      step="0.01"
                      className="w-24 px-2 py-1 bg-white border border-gray-200 rounded text-right text-sm font-medium focus:ring-1 focus:ring-orange-500 outline-none"
                      value={statutorySettings.pf_employer_rate}
                      onChange={(e) => setStatutorySettings({...statutorySettings, pf_employer_rate: parseFloat(e.target.value) || 0})}
                    />
                  ) : (
                    <span className="font-mono text-blue-600 font-medium">{Math.trunc(statutorySettings.pf_employer_rate)}%</span>
                  )}
                </div>
                <div className="flex justify-between items-center p-3 hover:bg-gray-50 rounded-lg transition-colors">
                  <span className="text-sm text-gray-600">Employer ESIC %</span>
                  {isStatutoryEditing ? (
                    <input 
                      type="number"
                      step="0.01"
                      className="w-24 px-2 py-1 bg-white border border-gray-200 rounded text-right text-sm font-medium focus:ring-1 focus:ring-orange-500 outline-none"
                      value={statutorySettings.esic_employer_rate}
                      onChange={(e) => setStatutorySettings({...statutorySettings, esic_employer_rate: parseFloat(e.target.value) || 0})}
                    />
                  ) : (
                    <span className="font-mono text-blue-600 font-medium">{Math.trunc(statutorySettings.esic_employer_rate)}%</span>
                  )}
                </div>
              </div>
            </div>


          </div>
        </div>
      </div>

      {/* Add Component Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm transition-all">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-300">
            <div className="p-5 border-b border-gray-50 flex items-center gap-4 bg-gray-50/50">
              <div className="p-3 bg-blue-100 text-blue-600 rounded-2xl">
                {isEditing ? <FaEdit /> : <FaPlus />}
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{isEditing ? "Edit Component" : "Add New Component"}</h3>
                <p className="text-[12px] text-gray-500">{isEditing ? "Update component details" : `Add a formula or fixed value to ${structures.find(s => s.id === activeStructureId)?.name}`}</p>
              </div>
            </div>
            
            <form onSubmit={handleSubmitComponent} className="p-8 space-y-6">
              {modalError && (
                <div className="p-4 bg-red-50 border border-red-100 rounded-2xl animate-in fade-in slide-in-from-top-2">
                  <p className="text-sm text-red-600 font-medium flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                    {modalError}
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider pl-1">Component Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Project Allowance"
                  className="w-full px-5 py-4 bg-white border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all font-medium text-gray-700"
                  value={form.name}
                  onChange={e => {
                    const name = e.target.value;
                    const autoCode = name.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
                    setForm({...form, name, code: form.code ? form.code : autoCode});
                  }}
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider pl-1">Unique Code (reference)</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. project_allowance"
                  className="w-full px-5 py-4 bg-white border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all font-medium text-gray-700"
                  value={form.code}
                  onChange={e => setForm({...form, code: e.target.value.toLowerCase().replace(/\s+/g, "_")})}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider pl-1">Type</label>
                  <select
                    className="w-full px-5 py-4 bg-white border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all font-medium text-gray-700 appearance-none"
                    value={form.type}
                    onChange={e => setForm({...form, type: e.target.value})}
                  >
                    <option value="earning">Earning</option>
                    <option value="deduction">Deduction</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider pl-1">Calculation</label>
                  <select
                    className="w-full px-5 py-4 bg-white border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all font-medium text-gray-700 appearance-none"
                    value={form.calc}
                    onChange={e => setForm({...form, calc: e.target.value})}
                  >
                    <option value="fixed">Fixed Amount</option>
                    <option value="percentage">Percentage</option>
                    <option value="balancing">Auto-Balancing</option>
                  </select>
                </div>
              </div>

              {form.calc !== 'balancing' && (
                <div className="space-y-2 animate-in slide-in-from-top-2">
                  <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider pl-1">
                    {form.calc === 'fixed' ? 'Amount (₹)' : 'Value (%)'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    placeholder={form.calc === 'fixed' ? '0.00' : '5'}
                    className="w-full px-5 py-4 bg-white border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all font-medium text-gray-700"
                    value={form.value}
                    onChange={e => setForm({...form, value: e.target.value})}
                  />
                </div>
              )}

              {form.calc === 'percentage' && (
                <div className="space-y-2 animate-in slide-in-from-top-2">
                  <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider pl-1">Base Component (Codes)</label>
                  <input
                    type="text"
                    placeholder="basic, da (comma separated)"
                    className="w-full px-5 py-4 bg-white border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all font-medium text-gray-700"
                    value={form.base.join(', ')}
                    onChange={e => setForm({...form, base: e.target.value.split(',').map(s => s.trim())})}
                  />
                </div>
              )}

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-3 py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 font-semibold rounded-xl transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-3 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-xl shadow-lg shadow-blue-200 transition-all flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <FaSync className="animate-spin" size={14} />
                      Processing...
                    </>
                  ) : (
                    isEditing ? "Update Component" : "Add Component"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200">
             <div className="p-8 text-center">
                <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaTrash size={24} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Delete Component?</h3>
                <p className="text-sm text-gray-500 mb-6">
                  Are you sure you want to delete <span className="font-semibold text-gray-700">"{deleteTarget?.componentName}"</span>? This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setDeleteTarget(null);
                    }}
                    className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 font-semibold rounded-lg transition-all text-sm"
                  >
                    Keep it
                  </button>
                  <button
                    onClick={confirmDelete}
                    disabled={loading}
                    className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg shadow-lg shadow-red-100 transition-all text-sm flex items-center justify-center gap-2"
                  >
                    {loading ? (
                       <FaSync className="animate-spin" size={12} />
                    ) : (
                      "Delete"
                    )}
                  </button>
                </div>
             </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryConfiguration;
